import React, { type ReactNode, useMemo } from "react";
import { StyleSheet, type TextStyle, View, type ViewProps, type ViewStyle } from "react-native";

import { LinearGradient } from "../../shared/components/LinearGradient/linear-gradient";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";

export type TagStatus = "success" | "alert" | "warning" | "neutral" | "info" | "ai";
export type TagVariant = "solid" | "light";
export const TAG_STATUSES: TagStatus[] = ["success", "alert", "warning", "neutral", "info", "ai"];
export const TAG_VARIANTS: TagVariant[] = ["solid", "light"];

const STATUS_TOKEN_MAP = {
  success: "StatusSuccess",
  alert: "StatusCritical",
  warning: "StatusWarning",
  neutral: "StatusGeneric",
  info: "StatusInfo",
  ai: "StatusInfo",
} as const;

export interface TagProps extends ViewProps {
  /**
   * The status color of the Tag.
   */
  status?: TagStatus;
  /**
   * The style variant of the Tag.
   */
  variant?: TagVariant;
  /**
   * Optional icon element to display before the children.
   */
  icon?: ReactNode;
  /**
   * Optional content to render after the main children.
   */
  contentAfter?: ReactNode;
  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle & TextStyle;
  /**
   * the content of the tag
   */
  children: ReactNode;
}

function componentWithColor(component: ReactNode, color: string) {
  if (!component) {
    return;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  return React.cloneElement(component as React.ReactElement<any>, { color });
}

export function Tag({
  status = "neutral",
  variant = "light",
  icon,
  contentAfter,
  sx,
  children,
}: TagProps): React.JSX.Element {
  const theme = useTheme();

  function getVariantStyle(
    status: TagStatus = "neutral",
    variant: TagVariant = "light",
  ): ViewStyle {
    const statusToken = STATUS_TOKEN_MAP[status];
    const variantStyles = {
      solid: {
        backgroundColor: theme.colors[`background${statusToken}`],
      },
      light: {
        backgroundColor: theme.colors[`background${statusToken}Weaker`],
      },
    };

    return variantStyles[variant];
  }

  const styles = useMemo(
    () =>
      StyleSheet.create({
        gradientWrapper: {
          overflow: "hidden", // Contains absolute positioning of the linear gradient
        },
        gradientFill: {
          ...StyleSheet.absoluteFillObject,
          borderRadius: theme.borderRadius.sm,
        },
        container: {
          alignContent: "center",
          alignItems: "center",
          justifyContent: "center",
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: "transparent",
          borderRadius: theme.borderRadius.sm,
          flexDirection: "row",
          paddingVertical: theme.spacing.scb1,
          paddingHorizontal: theme.spacing.s2,
          gap: theme.spacing.s1,
          minHeight: 24,
        },
        text: {
          fontWeight: "bold",
          fontSize: theme.fontSizes.f1,
        },
      }),
    [theme],
  );

  const style = StyleSheet.flatten([styles.container, getVariantStyle(status, variant), sx]);
  const statusToken = STATUS_TOKEN_MAP[status];

  // Determine text color
  let color: string;
  if (sx?.color) {
    color = sx.color.toString();
  } else if (variant === "solid") {
    color = theme.colors.foregroundInverse; // White text for solid variant
  } else {
    try {
      // Use the correct status token for foreground color lookup
      color = theme.colors[`foreground${statusToken}`];
    } catch (e) {
      color = theme.colors.foreground; // Default foreground
    }
  }

  const textColorStyle: TextStyle = { color };
  const textStyle = StyleSheet.flatten([styles.text, textColorStyle]);

  const content = (
    <>
      {componentWithColor(icon, color)}
      {/* This conditional check is used to only wrap the text in a Text component if the children is a string because of font scaling issue. */}
      {typeof children === "string" ? <Text sx={textStyle}>{children}</Text> : children}
      {componentWithColor(contentAfter, color)}
    </>
  );

  if (status === "ai") {
    // Use gradient background for AI tags
    const gradientColors: [string, string] =
      variant === "solid"
        ? [theme.colors.utilityAiStrongStart, theme.colors.utilityAiStrongEnd]
        : [theme.colors.utilityAiWeakStart, theme.colors.utilityAiWeakEnd];
    return (
      <View style={StyleSheet.flatten([styles.gradientWrapper, sx])}>
        <LinearGradient
          colors={gradientColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.gradientFill}
        />
        <View style={styles.container}>{content}</View>
      </View>
    );
  } else {
    // Use solid background for non-AI tags
    return <View style={style}>{content}</View>;
  }
}
